import React, { useState, FC } from 'react';
import { useAuth } from './AuthContext';
import {  X } from 'lucide-react';
import AvatarGrid from './AvatarGrid';

interface AvatarSelectorModalProps {
  onConfirm?: (avatarId: string | null) => Promise<void>; // onConfirm agora é opcional
  onClose?: () => void;
  isModal?: boolean;
  forceAvatarSelection?: boolean;
}

const AvatarSelector: FC<AvatarSelectorModalProps> = ({ onConfirm, onClose, isModal = false, forceAvatarSelection = false }) => {
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  
  const { user } = useAuth();
  
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const needsAvatar = forceAvatarSelection || (user?.role === 'paciente' || user?.role === 'terapeuta' || user?.role === 'administrador');

  const handleClickAvatar = (avatarId: string) => {
    if (selectedAvatar === avatarId) {
      handleConfirmClick();
      return;
    }

    setSelectedAvatar(avatarId);
  };

  const handleConfirmClick = async () => {
    setIsLoading(true);
    try {
      if (onConfirm) {
        await onConfirm(
          needsAvatar ? selectedAvatar : null
        );
      }
      
      if (!user?.uid || (needsAvatar && !selectedAvatar)) {
        return;
      }

      sessionStorage.setItem('selectedAvatar', selectedAvatar);
    } catch (error) {
      console.error('Erro ao confirmar seleção:', error);
      // Você pode adicionar um feedback de erro para o usuário aqui
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageError = (avatarId: string) => {
    setImageLoadErrors(prev => new Set(prev).add(avatarId));
  };
  
  const canConfirm = () => {
    if (!needsAvatar) return true;
    return selectedAvatar !== '';
  };

  const getButtonText = () => {
    if (isLoading) return 'Processando...';
    if (isModal) return 'Confirmar e Participar';
    return user?.role === 'terapeuta' ? 'Iniciar Sessão' : 'Entrar na Sessão';
  };
  
  const getInstructionMessage = () => {
    if (!needsAvatar) return 'Você entrará na sala como observador.';
    return user?.role === 'terapeuta' ? 'Como terapeuta, selecione um avatar para conduzir a sessão' : 'Selecione um avatar para te representar na sessão';
  };

  const mainContent = (
      <div className="bg-white rounded-3xl shadow-2xl max-w-7xl mx-auto overflow-hidden min-h-[80vh]">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Seção Esquerda */}
          <div className="flex-1 p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
            <div className="mb-8 sm:mb-12">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                {needsAvatar ? 'Selecione um Avatar' : 'Bem-vindo à Sessão'}
              </h1>
              <p className="text-base sm:text-lg text-gray-600">{getInstructionMessage()}</p>
              <small><b>(Clique duas vezes para confirmar)</b></small>
            </div>

            {needsAvatar && (
                <AvatarGrid
                  selectedAvatarId={selectedAvatar}
                  onSelect={handleClickAvatar}
                  disabled={isLoading}
                  gridClassName="max-w-2xl"
                  selectedHint="(Clique 2x para confirmar)"
                  imageErrorAvatarIds={imageLoadErrors}
                  onImageError={handleImageError}
                />
            )}
          </div>
        </div>
      </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[100] flex justify-center items-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
                <h2 className="text-2xl font-bold text-gray-800">Escolha seu Avatar</h2>
                <p className="text-gray-600 mt-1">Selecione o avatar que irá te representar na sessão.</p>
            </div>
            <div className="p-6 overflow-y-auto">
                <AvatarGrid
                  selectedAvatarId={selectedAvatar}
                  onSelect={handleClickAvatar}
                  disabled={isLoading}
                  variant="compact"
                  imageErrorAvatarIds={imageLoadErrors}
                  onImageError={handleImageError}
                />
            </div>
            <div className="p-6 border-t mt-auto flex justify-end items-center gap-4">
                <button onClick={onClose} disabled={isLoading} className="px-6 py-2 text-gray-700 font-semibold rounded-full hover:bg-gray-100 transition-colors">
                    Cancelar
                </button>
                <button onClick={handleConfirmClick} disabled={!canConfirm() || isLoading} className="px-8 py-3 bg-teal-600 text-white font-bold rounded-full hover:bg-teal-700 transition-colors disabled:bg-gray-400 flex justify-center items-center">
                    {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : getButtonText()}
                </button>
            </div>
            {onClose && (
                <button onClick={onClose} className="absolute -top-3 -right-3 bg-white rounded-full p-2 shadow-lg hover:bg-gray-200 z-10">
                    <X size={24} className="text-gray-800"/>
                </button>
            )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 via-blue-50 to-purple-100 flex items-center justify-center p-4">
      {mainContent}
    </div>
  );
};

export default AvatarSelector;
